<template>
  <div class="ou-selector">
    <!-- Show selector if user has accessible OUs -->
    <VaSelect
      v-if="showSelector"
      v-model="selectedOU"
      :options="ouOptions"
      :loading="isLoading"
      :disabled="!canSwitch || isLoading"
      track-by="value"
      text-by="text"
      value-by="value"
      placeholder="Select Organization"
      class="ou-select"
      :class="{ 'ou-select--single': !canSwitch }"
      @update:modelValue="handleOUChange"
    >
      <template #prepend>
        <VaIcon name="business" size="small" class="mr-1" />
      </template>
    </VaSelect>

    <!-- Show warning if no OUs available (non-super admin with no OU access) -->
    <VaChip
      v-else-if="!isLoading && ouStore.initialized"
      color="warning"
      size="small"
      icon="warning"
      class="no-ou-chip"
    >
      No OU Access
    </VaChip>

    <!-- Confirmation Modal -->
    <VaModal
      v-model="showConfirmModal"
      title="Switch Organization"
      size="small"
      :mobile-fullscreen="false"
      hide-default-actions
    >
      <p class="mb-4">
        Are you sure you want to switch to <strong>{{ pendingOUName }}</strong
        >?
      </p>
      <p class="text-secondary text-sm">Any unsaved changes will be lost.</p>

      <template #footer>
        <div class="flex gap-2 justify-end">
          <VaButton preset="secondary" @click="cancelSwitch">Cancel</VaButton>
          <VaButton color="primary" :loading="switching" @click="confirmSwitch">Switch</VaButton>
        </div>
      </template>
    </VaModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vuestic-ui'
import { useOUStore } from '../../../stores/ou-store'
import { loadUserMenus } from '../../sidebar/NavigationRoutes'

const router = useRouter()
const route = useRoute()
const { init: toast } = useToast()
const ouStore = useOUStore()

const selectedOU = ref<string | null>(null)
const showConfirmModal = ref(false)
const pendingOUCode = ref<string | null>(null)
const pendingOUName = ref<string>('')
const switching = ref(false)

// Computed properties from store
const isLoading = computed(() => ouStore.loading)
const canSwitch = computed(() => ouStore.canSwitchOU)
const showSelector = computed(() => ouStore.accessibleOUs.length > 0)

// Transform OUs to select options format
const ouOptions = computed(() => {
  return ouStore.accessibleOUs.map((ou) => ({
    text: ou.ou_name,
    value: ou.ou_code,
  }))
})

// Initialize on mount
onMounted(async () => {
  if (!ouStore.initialized) {
    await ouStore.initialize()
  }
  // Set initial selection
  if (ouStore.activeOUCode) {
    selectedOU.value = ouStore.activeOUCode
  }
})

// Watch for store changes
watch(
  () => ouStore.activeOUCode,
  (newCode) => {
    if (newCode && newCode !== selectedOU.value) {
      selectedOU.value = newCode
    }
  },
)

// Watch for initialization complete
watch(
  () => ouStore.initialized,
  (isInit) => {
    if (isInit && ouStore.activeOUCode) {
      selectedOU.value = ouStore.activeOUCode
    }
  },
)

function handleOUChange(newOUCode: string | null) {
  if (!newOUCode || newOUCode === ouStore.activeOUCode) {
    return
  }

  const ou = ouStore.accessibleOUs.find((o) => o.ou_code === newOUCode)
  if (!ou) {
    return
  }

  // Store pending change and show confirmation
  pendingOUCode.value = newOUCode
  pendingOUName.value = ou.ou_name
  showConfirmModal.value = true

  // Revert visual selection until confirmed
  selectedOU.value = ouStore.activeOUCode
}

function cancelSwitch() {
  showConfirmModal.value = false
  pendingOUCode.value = null
  pendingOUName.value = ''
  selectedOU.value = ouStore.activeOUCode
}

async function confirmSwitch() {
  if (!pendingOUCode.value) return

  switching.value = true
  const ouCode = pendingOUCode.value
  const ouName = pendingOUName.value

  try {
    const success = await ouStore.setActiveOU(ouCode)

    if (success) {
      selectedOU.value = ouCode

      // Force reload menus for new OU context (bypass cache)
      await loadUserMenus(true)

      toast({
        message: `Switched to ${ouName}`,
        color: 'success',
      })

      // Redirect to dashboard to avoid permission issues
      if (route.path !== '/admin/dashboard') {
        router.push('/admin/dashboard')
      }
    } else {
      toast({
        message: 'Failed to switch organization. Access denied.',
        color: 'danger',
      })
      selectedOU.value = ouStore.activeOUCode
    }
  } catch (error) {
    console.error('OU switch error:', error)
    toast({
      message: 'An error occurred while switching organization',
      color: 'danger',
    })
    selectedOU.value = ouStore.activeOUCode
  } finally {
    switching.value = false
    showConfirmModal.value = false
    pendingOUCode.value = null
    pendingOUName.value = ''
  }
}
</script>

<style lang="scss" scoped>
.ou-selector {
  min-width: 200px;
  max-width: 280px;

  @media (max-width: 640px) {
    min-width: 150px;
    max-width: 200px;
  }
}

.no-ou-chip {
  cursor: help;
}

.ou-select {
  --va-select-border-color: var(--va-primary);

  :deep(.va-input-wrapper__field) {
    background: var(--va-background-element);
    border-radius: 8px;
  }

  &--single {
    :deep(.va-select__dropdown-icon) {
      display: none;
    }
  }
}

.ou-option {
  display: flex;
  flex-direction: column;
  padding: 4px 0;

  &__name {
    font-weight: 500;
  }

  &__desc {
    font-size: 0.75rem;
    color: var(--va-secondary);
  }
}
</style>
